import catalog from "@shared/remediation/catalog.json";

export const REMEDIATION_URGENCY_GROUPS = Object.freeze({
  CRITICAL: Object.freeze({ key: "fix_now", label: "Fix now", severities: Object.freeze(["CRITICAL"]) }),
  HIGH: Object.freeze({ key: "fix_this_sprint", label: "Fix this sprint", severities: Object.freeze(["HIGH"]) }),
  MEDIUM: Object.freeze({ key: "plan_improve", label: "Plan improve", severities: Object.freeze(["MEDIUM"]) }),
  LOW: Object.freeze({ key: "hygiene", label: "Hygiene", severities: Object.freeze(["LOW"]) }),
});

const GROUP_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

export const LEGACY_SEVERITY_CAVEAT =
  "Grouped from legacy finding buckets — urgency bands may not match calibrated severity. Re-run assessment for accurate prioritization.";

const CATALOG_BY_RULE = new Map((catalog.entries || []).map((entry) => [String(entry.rule_id), entry]));

export function hasCalibratedSeverity(snapshot) {
  return Array.isArray(snapshot?.assessment?.findings) && snapshot.assessment.findings.length > 0;
}

function normalizeProvider(providerId) {
  return String(providerId || "").trim().toLowerCase();
}

function normalizeSeverity(severity) {
  const upper = String(severity || "").toUpperCase();
  if (upper === "CRITICAL" || upper === "HIGH" || upper === "MEDIUM" || upper === "LOW") return upper;
  if (upper === "WARNING" || upper === "WARN") return "MEDIUM";
  if (upper === "INFO") return "LOW";
  return "MEDIUM";
}

function normalizeStepList(value) {
  return Array.isArray(value) ? value.filter((x) => typeof x === "string" && x.trim()) : [];
}

export function resolveRemediation(ruleId, providerId) {
  const id = String(ruleId || "");
  if (!id) return null;
  const entry = CATALOG_BY_RULE.get(id);
  if (!entry) return null;

  const pid = normalizeProvider(providerId);
  const providerSteps = normalizeStepList(pid ? entry?.remediation_steps?.[pid] : null);
  const defaultSteps = normalizeStepList(entry?.remediation_steps?.default);

  return {
    rule_id: entry.rule_id,
    remediation_summary: entry.remediation_summary,
    remediation_steps: providerSteps.length > 0 ? providerSteps : defaultSteps,
    effort_hint: entry.effort_hint || null,
    references: Array.isArray(entry.references) ? entry.references : [],
    validation_status: entry.validation_status || null,
    provider_used: providerSteps.length > 0 ? pid : "default",
  };
}

export function collectOpenFindings(snapshot) {
  if (hasCalibratedSeverity(snapshot)) {
    const openFromAssessment = snapshot.assessment.findings.filter(
      (f) => String(f?.status || "").toUpperCase() === "OPEN",
    );
    return { findings: openFromAssessment, severity_source: "calibrated" };
  }

  const legacy = snapshot?.findings || {};
  const buckets = [
    { items: legacy.critical, defaultSeverity: "CRITICAL" },
    { items: legacy.warnings, defaultSeverity: "MEDIUM" },
    { items: legacy.other, defaultSeverity: "LOW" },
  ];
  const collected = [];
  for (const bucket of buckets) {
    const arr = Array.isArray(bucket.items) ? bucket.items : [];
    for (const f of arr) {
      if (!f || typeof f !== "object") continue;
      const ruleId = f.ruleId || f.rule_id || null;
      collected.push({
        rule_id: ruleId,
        ruleId,
        title: f.title || "",
        severity: f.severity || bucket.defaultSeverity,
        status: "OPEN",
      });
    }
  }
  return { findings: collected, severity_source: "legacy_buckets" };
}

export function buildActionableSummary(snapshot, providerId) {
  const { findings: openFindings, severity_source } = collectOpenFindings(snapshot);

  const grouped = {};
  for (const key of GROUP_ORDER) {
    grouped[key] = {
      urgency_key: REMEDIATION_URGENCY_GROUPS[key].key,
      urgency_label: REMEDIATION_URGENCY_GROUPS[key].label,
      severity: key,
      count: 0,
      findings: [],
    };
  }

  for (const finding of openFindings) {
    const groupKey = GROUP_ORDER.includes(normalizeSeverity(finding?.severity))
      ? normalizeSeverity(finding?.severity)
      : "MEDIUM";
    grouped[groupKey].findings.push({
      rule_id: finding?.rule_id || finding?.ruleId || null,
      title: finding?.title || "",
      severity: groupKey,
      remediation: resolveRemediation(finding?.rule_id || finding?.ruleId, providerId),
    });
    grouped[groupKey].count += 1;
  }

  return {
    provider_id: normalizeProvider(providerId) || null,
    open_total: openFindings.length,
    severity_source,
    caveat: severity_source === "legacy_buckets" ? LEGACY_SEVERITY_CAVEAT : null,
    groups: GROUP_ORDER.map((key) => grouped[key]),
  };
}
